import { FC } from 'react';
import { Post } from '@libs/models';
import { Helmet } from 'react-helmet-async';

import { getAddress } from '@libs/utils';

interface PostSEOProps {
  post: Post | null;
}

// Map internal employment types to Google's required values
const mapEmploymentType = (commitmentType: string): string => {
  const typeMap: Record<string, string> = {
    fullTime: 'FULL_TIME',
    partTime: 'PART_TIME',
    contract: 'CONTRACTOR',
    temporary: 'TEMPORARY',
    intern: 'INTERN',
    volunteer: 'VOLUNTEER',
    perDiem: 'PER_DIEM',
  };
  return typeMap[commitmentType] || 'FULL_TIME';
};

// Map compensation types to Google's expected unit text
const mapCompensationUnit = (compensationType: string): string => {
  const unitMap: Record<string, string> = {
    hourly: 'HOUR',
    daily: 'DAY',
    weekly: 'WEEK',
    monthly: 'MONTH',
    annually: 'YEAR',
    yearly: 'YEAR',
  };
  return unitMap[compensationType?.toLowerCase()] || 'YEAR';
};

export const PostSEO: FC<PostSEOProps> = ({ post }) => {
  if (!post || !post?.job || !post?.job?.rooftop) {
    return null;
  }

  const title = post.job.title;
  const description = post.job.description;
  const url = `${window.location.origin}/jobs/${post.id}`;
  const address = getAddress(post.job.rooftop.location ?? '');

  const isConfidential = post.job.rooftop.isConfidential;
  const employerName = isConfidential ? 'Confidential' : post.job.rooftop.name;

  // Create baseSalary object according to Google's requirements
  const createBaseSalary = () => {
    if (!post.job?.compensationType) return undefined;

    const unit = mapCompensationUnit(post.job.compensationType);
    const hasMinMax = post.job.compensationMin && post.job.compensationMax;

    if (hasMinMax) {
      return {
        '@type': 'MonetaryAmount',
        currency: 'USD',
        value: {
          '@type': 'QuantitativeValue',
          minValue: post.job.compensationMin,
          maxValue: post.job.compensationMax,
          unitText: unit,
        },
      };
    } else if (post.job.compensationMin) {
      return {
        '@type': 'MonetaryAmount',
        currency: 'USD',
        value: {
          '@type': 'QuantitativeValue',
          value: post.job.compensationMin,
          unitText: unit,
        },
      };
    }
    return undefined;
  };

  const googleSchema = {
    '@context': 'https://schema.org/',
    '@type': 'JobPosting',
    title,
    description,
    identifier: {
      '@type': 'PropertyValue',
      name: employerName,
      value: post.id.toString(),
    },
    datePosted: new Date(post.createdAt).toISOString().split('T')[0],
    validThrough: post.endDate ? new Date(post.endDate).toISOString().split('T')[0] : undefined,
    employmentType: mapEmploymentType(post.job.commitmentType || 'fullTime'),
    hiringOrganization: {
      '@type': 'Organization',
      name: employerName,
      ...(isConfidential ? {} : { sameAs: post.job.rooftop.website }),
      logo: 'https://storage.googleapis.com/carcareerrs-cloud-storage-assets/logo-icon.png',
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        streetAddress: address.streetAddress,
        addressLocality: address.addressLocality,
        addressRegion: address.addressRegion,
        postalCode: address.postalCode,
        addressCountry: address.addressCountry,
      },
    },
    ...(createBaseSalary() && { baseSalary: createBaseSalary() }),
    ...(post.job.keywords &&
      post.job.keywords.length > 0 && {
        occupationalCategory: post.job.keywords,
      }),
    ...(post.job.benefits &&
      post.job.benefits.length > 0 && {
        jobBenefits: post.job.benefits,
      }),
    ...(employerName && {
      industry: 'Automotive',
    }),
    qualifications: description,
    responsibilities: description,
  };

  // Remove undefined values
  const cleanSchema = JSON.parse(
    JSON.stringify(googleSchema, (key, value) => (value === undefined ? undefined : value)),
  );

  return (
    <Helmet prioritizeSeoTags>
      <link rel="canonical" href={url} />
      <title>CARCAREERS.COM - {title}</title>

      {/* Open Graph */}
      <meta name="title" property="og:title" content={title} />
      <meta name="description" property="og:description" content={description} />
      <meta
        name="image"
        property="og:image"
        content={'https://storage.googleapis.com/carcareerrs-cloud-storage-assets/logo-icon.png'}
      />
      <meta property="og:url" content={url} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="CARCAREERS.COM" />
      <meta property="og:locale" content="en_US" />

      {/* Google Job Posting Structured Data */}
      <script type="application/ld+json">{JSON.stringify(cleanSchema, null, 2)}</script>
    </Helmet>
  );
};
